import { watch } from 'vue';
import { useRouteQuery } from '@vueuse/router';

import { useErrors } from '@/composables/useErrors';

/**
 * Surface `successMessage` / `errorMessage` query parameters as toasts.
 *
 * A server-side redirect is the only way an OAuth callback can report how it
 * went: the seller left for the provider and came back through a fresh page
 * load, so there is no in-flight request whose response could carry the reason.
 * The callbacks put it in the URL and this turns it into a toast, wherever they
 * chose to land.
 *
 * The parameter is cleared once shown, so a refresh — or a router push that
 * preserves the query — doesn't repeat it.
 */
export function useRouteMessages(): void {
    const { showSuccess, showWarning } = useErrors();

    const successMessage = useRouteQuery<string | null>('successMessage', null);
    const errorMessage = useRouteQuery<string | null>('errorMessage', null);

    watch(
        successMessage,
        (value) => {
            if (value) {
                showSuccess(value);
                successMessage.value = null;
            }
        },
        { immediate: true },
    );

    watch(
        errorMessage,
        (value) => {
            if (value) {
                // Every producer of this parameter today is a connection flow.
                showWarning(value, 'Connection Failed');
                errorMessage.value = null;
            }
        },
        { immediate: true },
    );
}
